import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Bell,
  AlertTriangle,
  Clock,
  FileText,
  Eye,
  Trash2,
  RotateCcw,
  Upload
} from 'lucide-react';
import { useNotificaciones } from '../../hooks/useNotificaciones';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { hasPermission, PERMISSIONS } from '../../utils/authRoles';
import { AREA_BADGE_CLASSES } from '../../hooks/useAreaCatalog';
import { PrereqArea } from '../../services/api';
import { NotificacionDocumento } from '../../types';
import { esConteoDiasCreible } from '../../utils/fechaVencimiento';

interface NotificacionesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Mismas 4 secciones que el Sidebar (components/layout/Sidebar.tsx): Dashboard, Personal,
// Servicios, Programación. Antes esta pestaña tenía su propio set (documentos/personal/
// servicios/auditoria/presisi) que no calzaba con la navegación del resto de la app —
// "Programación" quedaba mezclado dentro de "Personal" y no existía "Dashboard".
type Categoria = 'dashboard' | 'personal' | 'servicios' | 'programacion';

const CATEGORIA_LABEL: Record<Categoria, string> = {
  dashboard: '🏠 Dashboard',
  personal: '👤 Personal',
  servicios: '⚙️ Servicios',
  programacion: '📅 Programación',
};

// Sub-secciones = los subItems reales del Sidebar para cada sección (mismo texto, para que
// el filtro se sienta igual a la navegación). Dashboard no tiene subItems en el Sidebar.
//
// 'Documentos de empresa' NO es un subItem del Sidebar: es una pestaña de Pre-SISI, igual que
// 'Pre-SISI' (que sí es el enlace a esa app). Se lista acá porque el backend la manda como
// `metadata.seccion` en las notificaciones de solicitudes de documentos de empresa
// (routes/empresa-documento-solicitudes.js); sin estar en este mapa esas notificaciones no
// resolvían categoría y caían en Dashboard.
const SUBSECCIONES: Record<Categoria, string[]> = {
  dashboard: [],
  personal: ['Listado Personal', 'Estado Documentación', 'Pre-SISI', 'Documentos de empresa'],
  servicios: ['Cobertura', 'Visualización', 'Servicios', 'Acreditación'],
  programacion: ['Planificación', 'Resumen'],
};

// Mapa inverso sub-sección → categoría. Los 10 nombres de arriba son únicos entre las cuatro
// categorías, así que no hay ambigüedad. Existe para que `metadata.seccion` — la señal que el
// backend manda a propósito (ver services/notificacionesService.js callers en zc_backend) —
// pueda decidir también la CATEGORÍA, no solo la sub-sección.
//
// Sin esto la señal se perdía: una vacación/licencia sale como `personal.estado` con
// `seccion: 'Planificación'` (routes/personal-estados.js), pero la heurística por prefijo la
// mandaba a Personal, y ahí 'Planificación' no es una sub-sección válida, así que terminaba
// en "Listado Personal". Quien programa turnos filtraba por Programación y no la veía nunca.
const CATEGORIA_DE_SUBSECCION: Record<string, Categoria> = Object.entries(SUBSECCIONES)
  .reduce((acc, [categoria, subsecciones]) => {
    subsecciones.forEach(sub => { acc[sub] = categoria as Categoria; });
    return acc;
  }, {} as Record<string, Categoria>);

// Áreas responsables posibles (zc_temp.prerrequisito_responsable.area). El filtro se
// ofrece con estos tres valores + "sin área", igual que el de Estado de Documentación.
const AREAS: PrereqArea[] = ['RRHH', 'Prevención', 'N/A'];

// Tipos de notificación que hablan de un vencimiento, vengan del servidor
// (documentos.*, barrido de zc_backend/services/vencimientosService.js) o sean sintéticas
// del cliente (documento_*). Para estos el botón "Cargar en Pre-SISI" tiene sentido: lo
// que corresponde hacer es subir el reemplazo.
const esNotificacionDeVencimiento = (tipo?: string | null): boolean =>
  tipo === 'documento_vencido' || tipo === 'documento_por_vencer' ||
  tipo === 'documentos.vencido' || tipo === 'documentos.por_vencer';

export const NotificacionesModal: React.FC<NotificacionesModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  // Call hook once and cast to `any` to avoid type mismatches from incremental types
  const _notificacionesHook = useNotificaciones() as any;
  const {
    notificaciones,
    notificacionesNoLeidas,
    isLoading,
    marcarComoLeida,
    marcarTodasComoLeidas,
    marcarComoLeidaOptimistic,
    marcarTodasComoLeidasOptimistic,
    descartarNotificacionOptimistic,
    restaurarNotificacionOptimistic,
    getColorPrioridad,
    formatearFecha,
    navegarADocumentos,
    isMarkingAllAsRead,
    isDismissing,
    isRestoring,
    isLoadingPapelera,
    notificacionesTodas,
    notificacionesLeidas,
    notificacionesPapelera,
    noLeidasOcultas,
    setVerPapelera,
  } = _notificacionesHook;
  const localLeidas: Set<string> | undefined = _notificacionesHook.localLeidas;
  const { data: currentUser } = useCurrentUser();
  const isLeida = (n: NotificacionDocumento) => {
    try {
      return !!n.leida || (!!localLeidas && (localLeidas as Set<string>).has(n.id));
    } catch (e) {
      return !!n.leida;
    }
  };
  
  // also available: all notifications and read notifications
  // (useful to show history)

  const [filtroPrioridad, setFiltroPrioridad] = useState<'todas' | 'alta' | 'media' | 'baja'>('todas');
  const [filtroTipo, setFiltroTipo] = useState<'todas' | 'leidas' | 'no_leidas' | 'papelera'>('no_leidas');
  const [filtroCategoria, setFiltroCategoria] = useState<'todas' | Categoria>('todas');
  const [filtroSubcategoria, setFiltroSubcategoria] = useState<'todas' | string>('todas');
  // Área responsable del documento (RRHH / Prevención / N/A / sin área asignada) — permite
  // que cada área vea de un vistazo solo lo que le toca resolver.
  const [filtroArea, setFiltroArea] = useState<'todas' | PrereqArea | 'sin_area'>('todas');
  // Notificación cuyo detalle está abierto (panel del botón del ojo) — null = cerrado
  const [detalleNotificacion, setDetalleNotificacion] = useState<NotificacionDocumento | null>(null);

  // La sub-sección depende de la categoría elegida — si el usuario cambia de categoría,
  // una sub-sección de la categoría anterior ya no tiene sentido.
  useEffect(() => {
    setFiltroSubcategoria('todas');
  }, [filtroCategoria]);

  // La papelera se consulta al backend solo mientras el usuario la tiene seleccionada en
  // Estado — evita pedirla en cada apertura de la campana cuando nadie la está mirando.
  useEffect(() => {
    if (typeof setVerPapelera === 'function') {
      setVerPapelera(filtroTipo === 'papelera');
    }
  }, [filtroTipo, setVerPapelera]);

  // Cerrar modal con tecla Escape — si hay un detalle abierto, Escape cierra ese primero
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        if (detalleNotificacion) {
          setDetalleNotificacion(null);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevenir scroll del body cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, detalleNotificacion]);

  // Función para determinar la categoría de una notificación, alineada 1:1 con las 4
  // secciones del Sidebar. Cubre tanto los tipos sintéticos generados en cliente
  // (documento_*, personal_*, con guion bajo) como los reales persistidos en BD
  // (documentos.*, personal.*, programacion.*, presisi.*, con punto — ver
  // services/notificacionesService.js en el backend). "Pre-SISI" y "Estado Documentación"
  // caen en Personal porque así están anidados en el Sidebar; Auditoría y los avisos
  // manuales sin destino claro caen en Dashboard, que es la sección de "resumen general".
  const getCategoriaNotificacion = (notif: Pick<NotificacionDocumento, 'tipo' | 'entidad_tipo' | 'metadata'>): Categoria => {
    const tipo = notif.tipo || '';
    const entidad = notif.entidad_tipo || '';
    // La sección que declara el backend manda sobre la heurística por prefijo: es una señal
    // explícita de quien creó la notificación, no una adivinanza sobre el nombre del tipo.
    // Las sintéticas y las notificaciones anteriores a este campo no la traen y siguen el
    // camino de siempre.
    const categoriaDeclarada = notif.metadata?.seccion ? CATEGORIA_DE_SUBSECCION[notif.metadata.seccion] : undefined;
    if (categoriaDeclarada) return categoriaDeclarada;
    if (entidad === 'programacion_semanal' || tipo.startsWith('programacion')) return 'programacion';
    if (entidad === 'pre_sisi_documento' || tipo.startsWith('presisi.')) return 'personal';
    if (entidad === 'solicitud_documento_empresa' || tipo.startsWith('empresa_doc.')) return 'personal';
    if (tipo.startsWith('documento_') || tipo.startsWith('documentos.')) return 'personal';
    if (tipo.startsWith('personal_') || tipo.startsWith('personal.')) return 'personal';
    if (tipo.startsWith('servicios_') || tipo.startsWith('servicios.') || tipo === 'mantenimiento_proximo') return 'servicios';
    if (tipo.startsWith('acreditacion')) return 'servicios';
    return 'dashboard'; // auditoria_*, manual y cualquier tipo sin sección clara
  };

  // Sub-sección dentro de la categoría, para el filtro fino que faltaba (Personal >
  // Pre-SISI vs Estado Documentación vs Listado, etc). Las notificaciones emitidas por el
  // backend a partir de este cambio traen metadata.seccion con el texto exacto del subItem
  // del Sidebar (ver services/notificacionesService.js callers en zc_backend) — se usa esa
  // señal directa cuando viene y es una sub-sección válida de la categoría. Las
  // notificaciones sintéticas y las que ya existían en BD antes de este cambio no la traen,
  // así que caen en la heurística por prefijo de siempre.
  const getSubcategoriaNotificacion = (notif: Pick<NotificacionDocumento, 'tipo' | 'entidad_tipo' | 'metadata'>): string | null => {
    const tipo = notif.tipo || '';
    const entidad = notif.entidad_tipo || '';
    const categoria = getCategoriaNotificacion(notif);
    const seccionServidor = notif.metadata?.seccion;
    if (seccionServidor && SUBSECCIONES[categoria]?.includes(seccionServidor)) {
      return seccionServidor;
    }
    if (categoria === 'personal') {
      if (entidad === 'pre_sisi_documento' || tipo.startsWith('presisi.')) return 'Pre-SISI';
      if (entidad === 'solicitud_documento_empresa' || tipo.startsWith('empresa_doc.')) return 'Documentos de empresa';
      if (tipo.startsWith('documento_') || tipo.startsWith('documentos.')) return 'Estado Documentación';
      return 'Listado Personal';
    }
    if (categoria === 'servicios') {
      if (tipo === 'servicios_sin_personal') return 'Cobertura';
      if (tipo.startsWith('acreditacion')) return 'Acreditación';
      return 'Servicios';
    }
    if (categoria === 'programacion') {
      return 'Planificación'; // los tipos hoy no distinguen Planificación vs Resumen
    }
    return null; // Dashboard no tiene sub-secciones en el Sidebar
  };

  // Base según filtro de Estado (historial), antes de Categoría/Sección/Prioridad —
  // se reutiliza para la lista final y para los conteos que evitan ofrecer un filtro
  // que garantizadamente no tiene resultados.
  const baseSegunEstado: NotificacionDocumento[] = (() => {
    if (filtroTipo === 'papelera') return notificacionesPapelera || [];
    if (filtroTipo === 'leidas') return notificacionesLeidas || [];
    if (filtroTipo === 'no_leidas') return notificaciones;
    return notificacionesTodas || notificaciones;
  })();

  const cumpleFiltroArea = (notif: NotificacionDocumento) =>
    filtroArea === 'todas' || (filtroArea === 'sin_area' ? !notif.area : notif.area === filtroArea);

  const notificacionesFiltradas = baseSegunEstado.filter(notif => {
    const cumplePrioridad = filtroPrioridad === 'todas' || notif.prioridad === filtroPrioridad;
    const cumpleCategoria = filtroCategoria === 'todas' || getCategoriaNotificacion(notif) === filtroCategoria;
    const cumpleSubcategoria = filtroSubcategoria === 'todas' || getSubcategoriaNotificacion(notif) === filtroSubcategoria;
    return cumplePrioridad && cumpleCategoria && cumpleSubcategoria && cumpleFiltroArea(notif);
  });

  // Opciones del filtro de Área con su conteo, mismo criterio que subseccionesConDatos: solo
  // se ofrece un área que realmente tenga notificaciones bajo los demás filtros activos, para
  // que elegirla nunca dé una lista vacía indistinguible de un filtro roto. El filtro de área
  // no se auto-incluye en la base del conteo (si no, siempre daría 1 opción).
  const areasConDatos: { valor: PrereqArea | 'sin_area'; label: string; total: number }[] = (() => {
    const base = baseSegunEstado.filter(n =>
      (filtroPrioridad === 'todas' || n.prioridad === filtroPrioridad) &&
      (filtroCategoria === 'todas' || getCategoriaNotificacion(n) === filtroCategoria) &&
      (filtroSubcategoria === 'todas' || getSubcategoriaNotificacion(n) === filtroSubcategoria)
    );
    const opciones: { valor: PrereqArea | 'sin_area'; label: string; total: number }[] = AREAS.map(a => ({
      valor: a,
      label: String(a),
      total: base.filter(n => n.area === a).length,
    }));
    opciones.push({
      valor: 'sin_area',
      label: '— Sin área —',
      total: base.filter(n => !n.area).length,
    });
    return opciones.filter(o => o.total > 0);
  })();

  // Conteo por categoría (respeta Estado y Prioridad) — se muestra junto a cada opción del
  // selector para que una categoría en 0 se explique sola en vez de sentirse "rota".
  const conteoPorCategoria = (Object.keys(CATEGORIA_LABEL) as Categoria[]).reduce((acc, cat) => {
    acc[cat] = baseSegunEstado.filter(n =>
      (filtroPrioridad === 'todas' || n.prioridad === filtroPrioridad) && cumpleFiltroArea(n) && getCategoriaNotificacion(n) === cat
    ).length;
    return acc;
  }, {} as Record<Categoria, number>);

  // Sub-secciones con al menos una notificación real, con su conteo. Antes el selector de
  // Sección listaba las 4-2 sub-secciones fijas del Sidebar aunque ninguna notificación
  // pudiera caer ahí (ej. hoy nada distingue "Visualización" de "Cobertura", ni "Resumen" de
  // "Planificación" — ver getSubcategoriaNotificacion): elegir esas opciones siempre daba una
  // lista vacía, indistinguible de un filtro roto. Ahora solo se ofrecen las que sí aplican.
  const subseccionesConDatos: { nombre: string; total: number }[] = (() => {
    if (filtroCategoria === 'todas') return [];
    const enCategoria = baseSegunEstado.filter(n =>
      (filtroPrioridad === 'todas' || n.prioridad === filtroPrioridad) && cumpleFiltroArea(n) && getCategoriaNotificacion(n) === filtroCategoria
    );
    const conteo = new Map<string, number>();
    enCategoria.forEach(n => {
      const sub = getSubcategoriaNotificacion(n);
      if (sub) conteo.set(sub, (conteo.get(sub) || 0) + 1);
    });
    return SUBSECCIONES[filtroCategoria]
      .filter(sub => conteo.has(sub))
      .map(sub => ({ nombre: sub, total: conteo.get(sub)! }));
  })();

  const handleMarcarTodasComoLeidas = async () => {
    try {
      if (typeof marcarTodasComoLeidasOptimistic === 'function') {
        await marcarTodasComoLeidasOptimistic();
      } else {
        await marcarTodasComoLeidas.mutateAsync();
      }
    } catch (error) {
      console.error('Error al marcar todas las notificaciones como leídas:', error);
    }
  };

  // El ojo ya no navega directo: abre el detalle (persona, documento, tiempo vencido) con
  // el botón de ir a modificar adentro. Ver = archivar: abrir el detalle marca como leída Y
  // manda la notificación a papelera de inmediato, salvo que ya estemos parados en la
  // papelera (ahí no tiene sentido volver a descartarla).
  const handleVerDetalle = (notificacion: NotificacionDocumento) => {
    setDetalleNotificacion(notificacion);
    if (!isLeida(notificacion) && notificacion.id && typeof marcarComoLeidaOptimistic === 'function') {
      marcarComoLeidaOptimistic(notificacion.id).catch(() => {});
    }
    if (filtroTipo !== 'papelera' && notificacion.id && typeof descartarNotificacionOptimistic === 'function') {
      descartarNotificacionOptimistic(notificacion.id).catch(() => {});
    }
  };

  const handleDescartar = async (notificacionId: string) => {
    try {
      if (typeof descartarNotificacionOptimistic === 'function') {
        await descartarNotificacionOptimistic(notificacionId);
      }
    } catch (error) {
      console.error('Error al enviar notificación a la papelera:', error);
    }
  };

  const handleRestaurar = async (notificacionId: string) => {
    try {
      if (typeof restaurarNotificacionOptimistic === 'function') {
        await restaurarNotificacionOptimistic(notificacionId);
      }
    } catch (error) {
      console.error('Error al restaurar notificación desde la papelera:', error);
    }
  };

  // Acción principal de un aviso de vencimiento: ir a subir el reemplazo en Pre-SISI, con la
  // persona (y el requisito, si se conoce el tipo de documento) ya preseleccionados — ver
  // los parámetros ?rut/?tipo en src/presisi/PreSisiCargaPage.tsx. Es una navegación dura
  // (window.location) y no un <Link> porque Pre-SISI es un módulo con sesión propia
  // (src/services/session.ts): puede exigir su propio login antes de aterrizar.
  const handleCargarEnPreSisi = (notificacion: NotificacionDocumento) => {
    const rut = notificacion.personal_id || notificacion.metadata?.rut_persona;
    if (!rut) {
      console.warn('⚠️ No se pudo abrir Pre-SISI: la notificación no trae RUT');
      return;
    }
    const tipo = notificacion.metadata?.tipo_documento || notificacion.documento_nombre || '';
    const params = new URLSearchParams({ rut: String(rut) });
    if (tipo) params.set('tipo', String(tipo));
    onClose();
    window.location.href = `/pre-sisi?${params.toString()}`;
  };

  const handleAccion = async (notificacion: NotificacionDocumento) => {
    console.log('🔗 Ejecutando acción para notificación:', notificacion);
    try {
      if (notificacion.id) {
        if (typeof marcarComoLeidaOptimistic === 'function') {
          await marcarComoLeidaOptimistic(notificacion.id);
        } else {
          await marcarComoLeida.mutateAsync(notificacion.id);
        }
      }
    } catch (err) {
      console.warn('No se pudo marcar notificación como leída antes de la acción:', err);
    }

    // Cerrar el modal de notificaciones primero
    onClose();

    // Las notificaciones de BD traen entidad_tipo — es una señal más confiable que adivinar
    // por prefijo del `tipo`, así que se rutea por ahí primero. Las sintéticas (documentos
    // vencidos/por vencer, actualizaciones de personal) no traen entidad_tipo y siguen el
    // camino heurístico de siempre por categoría.
    if (notificacion.entidad_tipo === 'pre_sisi_documento') {
      const puedeRevisar = hasPermission(currentUser as any, PERMISSIONS.PRESISI_REVIEW);
      window.location.href = puedeRevisar ? '/pre-sisi' : '/pre-sisi/mis-solicitudes';
      return;
    }
    if (notificacion.entidad_tipo === 'programacion_semanal') {
      window.location.href = '/planificacion';
      return;
    }
    // Solicitud de documento de empresa: siempre a "Mis solicitudes". Ahí conviven los
    // dos lados del flujo —lo que la cuenta envió y, para quien puede revisar, lo que
    // tiene que resolver— así que no hay que elegir destino según el aviso ni según el
    // permiso de quien lo abre. "Documentos de empresa" quedó solo con el checklist.
    // La vista vive dentro de Pre-SISI, que tiene sesión propia — igual que el caso de
    // 'pre_sisi_documento' de arriba, puede pedir login al aterrizar.
    if (notificacion.entidad_tipo === 'solicitud_documento_empresa') {
      window.location.href = '/pre-sisi/mis-solicitudes';
      return;
    }

    // Navegar según la categoría/sub-sección de la notificación (useNavegacionDocumentos
    // despacha evento global cuando hay un personal_id resoluble).
    const categoria = getCategoriaNotificacion(notificacion);
    const subcategoria = getSubcategoriaNotificacion(notificacion);
    switch (categoria) {
      case 'personal':
        if (subcategoria === 'Pre-SISI') {
          window.location.href = '/pre-sisi/mis-solicitudes';
        } else if (notificacion.personal_id && notificacion.personal_id !== 'undefined') {
          navegarADocumentos(notificacion.personal_id, notificacion.personal_id);
        } else {
          console.warn('⚠️ No se pudo navegar: personal_id no disponible');
          window.location.href = '/personal';
        }
        break;
      case 'servicios':
        window.location.href = '/servicios';
        break;
      case 'programacion':
        window.location.href = '/planificacion';
        break;
      case 'dashboard':
      default:
        // Antes esto navegaba a '/', que redirige a '/login' (ver App.tsx) — sacaba a
        // cualquier usuario autenticado de la app por una notificación sin destino claro.
        window.location.href = '/dashboard';
    }
  };

  const getIconoPrioridad = (prioridad: 'alta' | 'media' | 'baja') => {
    switch (prioridad) {
      case 'alta':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'media':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'baja':
        return <FileText className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

         const modalContent = (
           <>
           <div
             className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 animate-fadeIn"
             onClick={handleBackdropClick}
             style={{ zIndex: 9999 }}
           >
             <div
               className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden transform transition-all duration-300 ease-out animate-slideInUp"
               onClick={(e) => e.stopPropagation()}
               style={{ zIndex: 10000 }}
             >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">Notificaciones</h2>
                <p className="text-blue-100 text-sm">
                  {notificacionesNoLeidas} notificaciones no leídas
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {notificacionesNoLeidas > 0 && (
                <button
                  onClick={handleMarcarTodasComoLeidas}
                  disabled={isMarkingAllAsRead}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isMarkingAllAsRead ? 'Marcando...' : 'Marcar como leídas'}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                title="Cerrar notificaciones (ESC)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex flex-wrap gap-4">
            {/* Filtro por categoría — mismas 4 secciones que el menú lateral */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Categoría:</label>
              <select
                value={filtroCategoria}
                onChange={(e) => setFiltroCategoria(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todas">Todas ({baseSegunEstado.length})</option>
                {(Object.keys(CATEGORIA_LABEL) as Categoria[]).map(cat => (
                  <option key={cat} value={cat}>{CATEGORIA_LABEL[cat]} ({conteoPorCategoria[cat]})</option>
                ))}
              </select>
            </div>

            {/* Filtro por sub-sección — solo aparece si la categoría elegida tiene alguna
                notificación real en al menos una de sus sub-secciones */}
            {filtroCategoria !== 'todas' && subseccionesConDatos.length > 0 && (
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Sección:</label>
                <select
                  value={filtroSubcategoria}
                  onChange={(e) => setFiltroSubcategoria(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="todas">Todas</option>
                  {subseccionesConDatos.map(sub => (
                    <option key={sub.nombre} value={sub.nombre}>{sub.nombre} ({sub.total})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Filtro por área responsable — solo aparece si hay notificaciones con área
                resuelta (las de vencimiento la traen; el resto no). Mismos valores que el
                filtro homónimo de Estado de Documentación. */}
            {areasConDatos.length > 0 && (
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Área:</label>
                <select
                  value={filtroArea}
                  onChange={(e) => setFiltroArea(e.target.value as 'todas' | PrereqArea | 'sin_area')}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="todas">Todas</option>
                  {areasConDatos.map(a => (
                    <option key={a.valor} value={a.valor}>{a.label} ({a.total})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Filtro por prioridad */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Prioridad:</label>
              <select
                value={filtroPrioridad}
                onChange={(e) => setFiltroPrioridad(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todas">Todas</option>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </div>

            {/* Filtro por estado */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Estado:</label>
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todas">Todas</option>
                <option value="no_leidas">No leídas</option>
                <option value="leidas">Leídas</option>
                <option value="papelera">Papelera</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto max-h-96">
          {(isLoading || (filtroTipo === 'papelera' && isLoadingPapelera)) ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Cargando notificaciones...</span>
            </div>
          ) : notificacionesFiltradas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Bell className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Sin resultados para estos filtros</p>
              <p className="text-sm">Prueba con otra categoría, sección o prioridad</p>
            </div>
          ) : (
            <div className="p-2 space-y-1.5">
              {notificacionesFiltradas.map((notificacion) => {
                const accentBorde = notificacion.prioridad === 'alta'
                  ? 'border-l-red-400'
                  : notificacion.prioridad === 'media'
                  ? 'border-l-yellow-400'
                  : 'border-l-blue-400';
                const iconoFondo = notificacion.prioridad === 'alta'
                  ? 'bg-red-50'
                  : notificacion.prioridad === 'media'
                  ? 'bg-yellow-50'
                  : 'bg-blue-50';
                return (
                <div
                  key={notificacion.id}
                  className={`grid grid-cols-[2rem_1fr_4.25rem_5.5rem_7rem_8rem_4.5rem] items-center gap-3 rounded-lg border border-gray-100 border-l-4 ${accentBorde} px-3 py-2.5 transition-shadow hover:shadow-sm ${
                      !isLeida(notificacion) ? 'bg-blue-50/40' : 'bg-white'
                    }`}
                >
                  {/* Icono por prioridad, en vez del emoji anterior — permite escanear la
                      lista por color sin leer cada fila */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconoFondo}`}>
                    {getIconoPrioridad(notificacion.prioridad)}
                  </div>

                  {/* Mensaje: única columna de ancho flexible — el resto son columnas de
                      ancho fijo (grid, no flex) para que ALTA/MEDIA/BAJA, la categoría, la
                      fecha y las acciones queden siempre alineadas entre filas sin importar
                      cuánto texto tenga el mensaje de cada una. */}
                  <div className="min-w-0 flex items-center gap-2 text-sm overflow-hidden">
                    {!isLeida(notificacion) && (
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" title="No leída"></span>
                    )}
                    {/* Para vencimientos el título ES el nombre del documento (ver
                        doc.nombre_documento en useNotificaciones.ts) — no debe verse en la
                        lista, solo en el detalle del ojo. El resto de tipos sí traen un
                        título con valor informativo propio (ej. "Vacaciones: Juan Pérez"). */}
                    {notificacion.tipo !== 'documento_vencido' && notificacion.tipo !== 'documento_por_vencer' && (
                      <>
                        <span className="font-semibold text-gray-900 truncate max-w-[45%] flex-shrink-0" title={notificacion.titulo}>
                          {notificacion.titulo}
                        </span>
                        <span className="text-gray-300 flex-shrink-0">·</span>
                      </>
                    )}
                    <span className="text-gray-600 truncate min-w-0" title={notificacion.mensaje}>
                      {notificacion.mensaje}
                    </span>
                  </div>

                  <span className={`justify-self-start px-1.5 py-0.5 font-medium rounded-full border text-[10px] ${getColorPrioridad(notificacion.prioridad)}`}>
                    {notificacion.prioridad.toUpperCase()}
                  </span>

                  {/* Área responsable — quién tiene que resolverlo. Mismos colores que el
                      badge de Estado de Documentación (AREA_BADGE_CLASSES) para que se lea
                      igual en las dos pantallas. Celda vacía (no ausente) cuando no hay área:
                      el grid tiene columnas fijas y omitirla correría todo lo que sigue. */}
                  {notificacion.area ? (
                    <span
                      className={`justify-self-start px-1.5 py-0.5 font-medium rounded-full text-[10px] truncate max-w-full ${AREA_BADGE_CLASSES[notificacion.area]}`}
                      title={`Área responsable: ${notificacion.area}`}
                    >
                      {notificacion.area}
                    </span>
                  ) : <span />}

                  {filtroCategoria === 'todas' ? (
                    <span className="justify-self-start px-1.5 py-0.5 font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-[10px] truncate max-w-full">
                      {CATEGORIA_LABEL[getCategoriaNotificacion(notificacion)]}
                    </span>
                  ) : <span />}

                  <span className="justify-self-start text-gray-400 text-xs">{formatearFecha(notificacion.fecha_creacion)}</span>

                  {/* Acciones: ver detalle (persona, documento, tiempo vencido) siempre
                      disponible; en la bandeja activa se puede enviar a papelera sin abrir
                      el detalle, en la papelera se puede restaurar */}
                  <div className="flex items-center gap-1 justify-self-end">
                    <button
                      onClick={() => handleVerDetalle(notificacion)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Ver detalle"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {filtroTipo === 'papelera' ? (
                      <button
                        onClick={() => handleRestaurar(notificacion.id)}
                        disabled={isRestoring}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors disabled:opacity-50"
                        title="Restaurar"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDescartar(notificacion.id)}
                        disabled={isDismissing}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                        title="Enviar a papelera"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — conteos por categoría ya están en el selector de arriba; acá solo lo
            que resume la vista actual, para no repetir la misma información dos veces */}
        <div className="px-6 py-3 bg-gray-50 border-t">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              Mostrando {notificacionesFiltradas.length} de {baseSegunEstado.length} · {notificacionesNoLeidas} no leídas
              {noLeidasOcultas > 0 && ` · +${noLeidasOcultas} más antiguas`}
            </span>
            <span>{new Date().toLocaleTimeString('es-ES')}</span>
          </div>
        </div>
      </div>
    </div>

    {/* Detalle del botón del ojo: persona, documento y tiempo vencido, con acceso directo
        a modificarlo — antes el ojo navegaba directo y perdía este contexto */}
    {detalleNotificacion && (
      <div
        className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10050] p-4"
        onClick={() => setDetalleNotificacion(null)}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4 border-b flex items-start justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-900">{detalleNotificacion.titulo}</h3>
            <button
              onClick={() => setDetalleNotificacion(null)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-5 py-4 space-y-2.5 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Persona</span>
              <span className="text-gray-900 font-medium text-right">{detalleNotificacion.personal_nombre || '—'}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Documento</span>
              <span className="text-gray-900 font-medium text-right">{detalleNotificacion.documento_nombre || detalleNotificacion.titulo}</span>
            </div>
            <div className="flex justify-between gap-3 items-center">
              <span className="text-gray-500">Área responsable</span>
              {detalleNotificacion.area ? (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${AREA_BADGE_CLASSES[detalleNotificacion.area]}`}>
                  {detalleNotificacion.area}
                </span>
              ) : (
                <span className="text-gray-400">Sin área asignada</span>
              )}
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Estado</span>
              <span className="text-gray-900 font-medium text-right">
                {esConteoDiasCreible(detalleNotificacion.dias_restantes)
                  ? ((detalleNotificacion.dias_restantes as number) < 0
                      ? `Vencido hace ${Math.abs(detalleNotificacion.dias_restantes as number)} días`
                      : `Vence en ${detalleNotificacion.dias_restantes} días`)
                  : detalleNotificacion.mensaje}
              </span>
            </div>
            <p className="text-gray-600 pt-1 border-t">{detalleNotificacion.mensaje}</p>
          </div>
          <div className="px-5 py-3 bg-gray-50 border-t flex justify-end gap-2">
            <button
              onClick={() => setDetalleNotificacion(null)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cerrar
            </button>
            <button
              onClick={() => {
                const notif = detalleNotificacion;
                setDetalleNotificacion(null);
                handleAccion(notif);
              }}
              className="px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 border border-blue-200 font-medium rounded-lg transition-colors"
            >
              Ver documento
            </button>
            {/* Acción principal cuando algo venció: no basta con mirarlo, hay que subir el
                reemplazo — y eso se hace por Pre-SISI, no editando el documento vencido. */}
            {esNotificacionDeVencimiento(detalleNotificacion.tipo) &&
              (detalleNotificacion.personal_id || detalleNotificacion.metadata?.rut_persona) && (
              <button
                onClick={() => {
                  const notif = detalleNotificacion;
                  setDetalleNotificacion(null);
                  handleCargarEnPreSisi(notif);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
              >
                <Upload className="w-4 h-4" /> Cargar en Pre-SISI
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );

  // Renderizar el modal usando un portal al body del documento
  return createPortal(modalContent, document.body);
};
